import { InfoCircleOutlined } from '@ant-design/icons';
import { taxationApi } from 'Api/taxation';
import { Col, Collapse, CollapseProps, Modal, Row, Tooltip } from 'antd';
import { PermissionContext } from 'components/Global/AuthLayout';
import Buttons from 'components/Global/Buttons';
import InputField from 'components/Global/InputField';
import SelectDropdown from 'components/Global/SelectDropdown';
import { currencyData } from 'constants/CurrencyData';
import { booleanOptions } from 'constants/Data';
import dayjs from 'dayjs';
import { TaxationInterface } from 'interfaces/employee.interface';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
	formatToDateOnly,
	hasFormError,
	invalidText,
	toastText,
	validateFormData,
} from 'utils/utils';
import styles from './index.module.scss';
type Props = {
	changeTab: (key: string) => void;
	onCancel: () => void;
	setIsUsd: any;
	setIsEmployeeTaxationExists: any;
};

const TaxationDetails = (props: Props) => {
	const permissionsContext = useContext(PermissionContext);

	const { changeTab, onCancel, setIsEmployeeTaxationExists } = props;
	const [employeeId, setEmployeeId] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const [isTaxationCreated, setIsTaxationCreated] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(false);
	const [previousTerminateDate, setPreviousTerminateDate] = useState('');

	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [changeTerminationSubmitLoading, setChangeTerminationSubmitLoading] =
		useState(false);
	const [isTerminationDateChange, setIsTerminationDateChange] =
		useState(false);
	const [isPayrollRun, setIsPayrollRun] = useState(false);
	const [previousCurrency, setPreviousCurrency] = useState('');
	const [isCurrencyChange, setIsCurrencyChange] = useState(false);
	const [payGroupOptions, setPayGroupOptions] = useState<any>([]);
	const [isPayGroupRequired, setIsPayGroupRequired] = useState(true);

	const [selectedPayGroupDetails, setSelectedPayGroupDetails] = useState<any>(
		{
			isLoading: false,
			selectedPayGroup: '',
		}
	);

	const [taxationDetails, setTaxationDetails] = useState<TaxationInterface>({
		employeeTPIN: '',
		companyCurrencyId: '',
		taxationMethod: '',
		employmentType: '',
		enableGrossUpTax: false,
		disabilities: false,
		terminationDate: null,
		isCompensationFund: false,
	});

	const [formError, setFormError] = useState<any>({
		employeeTPIN: false,
		companyCurrencyId: false,
		taxationMethod: false,
		employmentType: false,
		enableGrossUpTax: false,
		disabilities: false,
		isCompensationFund: false,
		payGroupId: false,
	});
	const constantDropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	const mappedCurrencyOptions = constantDropdownOptions.currencyData.map(
		(currency: any) => ({
			value: currency.value,
			label: `${currency.label} - ${
				currencyData[currency.label] || currency.label
			}`,
			// label: currencyData[currency.label] || currency.label
		})
	);

	const openConfirmationModal = () => {
		setShowConfirmationModal(true);
	};

	const closeConfirmationModal = () => {
		setShowConfirmationModal(false);
	};

	const handleOkForModal = async () => {
		await handleSubmit(true);
		if (changeTerminationSubmitLoading === false) {
			setShowConfirmationModal(false);
		}
	};

	const fetchPayGroupOptions = async (data: {
		employeeId: string;
		companyCurrencyId: string;
		isCurrencyChange: boolean;
	}) => {
		try {
			setSelectedPayGroupDetails((prev: any) => {
				return {
					...prev,
					isLoading: true,
				};
			});
			const response = await taxationApi.getPayGroupOptions(data);

			if (response?.data) {
				if (typeof response.data.isPayGroupRequired !== 'undefined') {
					setIsPayGroupRequired(response.data.isPayGroupRequired);
				}
			}

			if (response?.data?.data) {
				setPayGroupOptions(response.data.data);
				if (response.data.data.length > 0) {
					const findSelectedPayGroup = response.data?.data.find(
						(pg: any) => pg.isSelected
					);
					if (data.isCurrencyChange) {
						setSelectedPayGroupDetails({
							isLoading: false,
							selectedPayGroup:
								response.data.data[0]?.value || '',
						});
					} else {
						setSelectedPayGroupDetails({
							isLoading: false,
							selectedPayGroup: findSelectedPayGroup?.value || '',
						});
					}
				}
			}
		} catch {
			toastText('Failed to fetch pay group options', 'error');
		} finally {
			setSelectedPayGroupDetails((prev: any) => {
				return {
					...prev,
					isLoading: false,
				};
			});
		}
	};
	const confirmModalButtons = [
		{
			text: 'No',
			className: 'secondary-button',
			fontSize: '1.6rem',
			minWidth: '60px',
			minHeight: '20px',
			isSubmit: false,
			isLoading: false,
			disabled: changeTerminationSubmitLoading,
			onclick: closeConfirmationModal,
		},
		{
			text: 'Yes',
			isLoading: changeTerminationSubmitLoading,
			className: 'btn-blue',
			fontSize: '1.6rem',
			minWidth: '60px',
			minHeight: '20px',
			isSubmit: false,
			onclick: handleOkForModal,
		},
	];

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: false,
			disabled: isLoading,
			onclick: () => {
				onCancel();
			},
			size: 'small',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Save',
			isLoading: isLoading,
			className: 'btn-blue',
			isSubmit: false,
			disabled: isLoading,
			onclick: () => {
				checkForTerminateDate();
			},
			size: 'small',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Back',
			isLoading: false,
			className: 'secondary-btn',
			isSubmit: false,
			disabled: isLoading,
			onclick: () => {
				changeTab('employmentDetails');
			},
			size: 'small',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	const handleChange = async (
		value: string | number | null | string[] | boolean,
		name: string,
		stateName: string,
		required: boolean,
		regex?: RegExp | null
	) => {
		if (name === 'companyCurrencyId') {
			if (previousCurrency && previousCurrency !== value) {
				setIsCurrencyChange(true);
			}
			setSelectedPayGroupDetails({
				isLoading: false,
				selectedPayGroup: '',
			});
			await fetchPayGroupOptions({
				employeeId: employeeId as string,
				companyCurrencyId: value as string,
				isCurrencyChange: true,
			});
		}

		OnChange(value, name, stateName);
	};

	const OnChange = (
		value: string | number | null | string[] | boolean,
		key: string,
		stateName: string
	) => {
		switch (stateName) {
			case 'taxationDetails':
				setTaxationDetails((prev) => {
					return {
						...prev,
						[key]: value,
					};
				});
				break;
		}
		const checkFormError = validateFormData(
			{ [key]: value },
			{ ...formError }
		);
		setFormError(checkFormError);
	};

	const checkForTerminateDate = async () => {
		if (
			dayjs(taxationDetails.terminationDate).format('YYYY-MM-DD') !==
			dayjs(previousTerminateDate).format('YYYY-MM-DD')
		) {
			setIsTerminationDateChange(true);
			openConfirmationModal();
		} else {
			await handleSubmit(false);
		}
	};

	const handleSubmit = async (isTerminationChange: boolean) => {
		let checkFormError = validateFormData(
			{
				...taxationDetails,
			},
			{ ...formError }
		);
		if (
			isPayGroupRequired &&
			invalidText(selectedPayGroupDetails.selectedPayGroup)
		) {
			checkFormError = {
				...checkFormError,
				payGroupId: true,
			};
		}

		setFormError(checkFormError);

		if (hasFormError(checkFormError)) {
			return;
		} else {
			isTerminationChange
				? setChangeTerminationSubmitLoading(true)
				: setIsLoading(true);
			if (!isTaxationCreated) {
				if (searchParams.get('employeeId')) {
					try {
						const data: any = {
							...taxationDetails,
							payGroupId:
								selectedPayGroupDetails.selectedPayGroup,
							disabilities:
								(taxationDetails.disabilities as any as string) ===
									'true' ||
								(taxationDetails.disabilities as any) === true,
							isCompensationFund:
								(taxationDetails.isCompensationFund as any as string) ===
									'true' ||
								(taxationDetails.isCompensationFund as any) ===
									true,
							enableGrossUpTax:
								(taxationDetails.enableGrossUpTax as any as string) ===
									'true' ||
								(taxationDetails.enableGrossUpTax as any) ===
									true,
							employeeTPIN: Number(taxationDetails.employeeTPIN),
							id: searchParams.get('employeeId'),
							terminationDate: taxationDetails?.terminationDate
								? formatToDateOnly(
										taxationDetails?.terminationDate
								  )
								: null,
						};

						if (invalidText(data.terminationDate)) {
							delete data['terminationDate'];
						}

						const createdEmployee =
							await taxationApi.createTaxation(data);
						setIsEmployeeTaxationExists(true);
						const message =
							createdEmployee?.data?.message ||
							'Employee taxation created successfully';
						props.setIsUsd(createdEmployee.data.isUsd);
						toastText(message, 'success');
						getTaxationDetail(
							searchParams.get('employeeId') as string
						);
						setIsTaxationCreated(true);
						if (isTerminationDateChange) {
							changeTab('earnings');
						}
					} catch (err: any) {
						const message =
							err?.response?.data?.message ||
							'Something went wrong in creating taxation';
						toastText(message, 'error');
					}
				} else {
					toastText(
						'Create an employee before adding taxation details',
						'error'
					);
				}
			} else if (searchParams.get('employeeId')) {
				const updateData = {
					...taxationDetails,
					payGroupId: selectedPayGroupDetails.selectedPayGroup,
					employeeId: searchParams.get('employeeId') as string,
					disabilities:
						(taxationDetails.disabilities as any as string) ===
							'true' ||
						(taxationDetails.disabilities as any) === true,
					isCompensationFund:
						(taxationDetails.isCompensationFund as any as string) ===
							'true' ||
						(taxationDetails.isCompensationFund as any) === true,
					enableGrossUpTax:
						(taxationDetails.enableGrossUpTax as any as string) ===
							'true' ||
						(taxationDetails.enableGrossUpTax as any) === true,
					employeeTPIN: Number(taxationDetails.employeeTPIN),
					terminationDate: taxationDetails?.terminationDate
						? formatToDateOnly(taxationDetails?.terminationDate)
						: null,
				};

				try {
					const updatedEmployeeTaxation =
						await taxationApi.updateTaxation(
							searchParams.get('employeeId') as string,
							updateData
						);

					setIsEmployeeTaxationExists(true);
					const message =
						updatedEmployeeTaxation?.data?.message ||
						'Employee taxation updated successfully';
					props.setIsUsd(updatedEmployeeTaxation.data.isUsd);
					toastText(message, 'success');
					getTaxationDetail(searchParams.get('employeeId') as string);
					if (isTerminationDateChange) {
						changeTab('earnings');
					}
				} catch (err: any) {
					const message = err?.response?.data?.message;
					toastText(
						message || 'Something went wrong in updating taxation',
						'error'
					);
				}
			} else {
				toastText(
					'Create an employee before adding taxation details',
					'error'
				);
			}

			isTerminationChange
				? setChangeTerminationSubmitLoading(false)
				: setIsLoading(false);
		}
	};

	useEffect(() => {
		const employeeIdFromSearchParams = searchParams.get('employeeId');
		if (employeeIdFromSearchParams) {
			setEmployeeId(employeeIdFromSearchParams);
			getTaxationDetail(employeeIdFromSearchParams);
		}
	}, []);

	const getTaxationDetail = async (employeeId: string) => {
		try {
			const taxationData =
				await taxationApi.getTaxationDetailsByEmployeeId(employeeId);
			if (taxationData?.data?.data) {
				await fetchPayGroupOptions({
					employeeId: employeeId,
					companyCurrencyId:
						taxationData?.data?.data.companyCurrencyId,
					isCurrencyChange: false,
				});
				setIsTaxationCreated(true);
				const updatedTaxationDetails: any = { ...taxationDetails };
				setPreviousTerminateDate(
					taxationData?.data?.data?.terminationDate
				);
				setPreviousCurrency(
					taxationData?.data?.data?.companyCurrencyId
				);

				for (const key in taxationData?.data?.data) {
					if (key in taxationDetails) {
						updatedTaxationDetails[key] =
							taxationData?.data?.data[key];
					}
				}

				setTaxationDetails({
					...updatedTaxationDetails,
					terminationDate: !invalidText(
						updatedTaxationDetails.terminationDate
					)
						? dayjs(updatedTaxationDetails.terminationDate)
						: null,
				});

				setIsPayrollRun(taxationData.data.payrollRun);
			} else {
				setIsTaxationCreated(false);
			}
		} catch (error: any) {
			toastText(
				error?.response?.data?.message ||
					'Something went wrong in fetching employee details',
				'error'
			);
		}
	};

	const items: CollapseProps['items'] = [
		{
			key: '1',
			label: (
				<div
					style={{ fontSize: '1.8rem', fontWeight: 500 }}
					className="color-purple"
				>
					Taxation Details
				</div>
			),
			children: (
				<div className={styles['form-container']}>
					<Row className={styles['form-container-card']} gutter={50}>
						<Col span={6} className={styles['col']}>
							<InputField
								name="employeeTPIN"
								value={taxationDetails.employeeTPIN}
								label="Employee TPIN "
								required={true}
								// helperText="Employee TPIN must be a 10-digit number"
								onChange={(value) => {
									handleChange(
										value,
										'employeeTPIN',
										'taxationDetails',
										true
									);
								}}
								// regex="^\d{10}$"
								isError={formError.employeeTPIN}
								disabled={isDisabled}
							/>
						</Col>

						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Currency"
								options={mappedCurrencyOptions}
								value={taxationDetails.companyCurrencyId}
								onChange={(value) => {
									handleChange(
										value,
										'companyCurrencyId',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Currency is required"
								label="Currency"
								disabled={isDisabled || isPayrollRun}
								extraLabel={
									isPayrollRun ? (
										<Tooltip
											title={
												'You can change the currency after a current payroll is approved/finalized'
											}
											placement="top"
										>
											<InfoCircleOutlined
												style={{
													paddingInline: '0.5rem',
													color: '#584495',
												}}
											/>
										</Tooltip>
									) : isCurrencyChange ? (
										<Tooltip
											title={
												'Currency change removes employee from pay group'
											}
											placement="top"
										>
											<InfoCircleOutlined
												style={{
													paddingInline: '0.5rem',
													color: '#584495',
												}}
											/>
										</Tooltip>
									) : null
								}
								isError={formError.companyCurrencyId}
							/>
						</Col>
						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Pay Group"
								options={payGroupOptions}
								loading={selectedPayGroupDetails.isLoading}
								value={String(
									selectedPayGroupDetails.selectedPayGroup
								)}
								onChange={(value) => {
									setFormError((prev: any) => {
										return {
											...prev,
											payGroupId: false,
										};
									});
									setSelectedPayGroupDetails({
										isLoading: false,
										selectedPayGroup: value,
									});
								}}
								size="large"
								required={isPayGroupRequired}
								helperText={
									isPayGroupRequired
										? 'Pay Group is Required'
										: ''
								}
								label="Pay Group"
								disabled={
									isDisabled ||
									isPayrollRun ||
									selectedPayGroupDetails.isLoading
								}
								extraLabel={
									isPayrollRun && (
										<Tooltip
											title={
												'You can change the pay group after a current payroll is approved/finalized'
											}
											placement="top"
										>
											<InfoCircleOutlined
												style={{
													paddingInline: '0.5rem',
													color: '#584495',
												}}
											/>
										</Tooltip>
									)
								}
								isError={formError.payGroupId}
							/>
						</Col>

						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Taxation Method"
								options={constantDropdownOptions.taxationMethod}
								value={taxationDetails.taxationMethod}
								onChange={(value) => {
									handleChange(
										value,
										'taxationMethod',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Taxation method is required"
								label="Taxation Method"
								disabled={isDisabled}
								isError={formError.taxationMethod}
							/>
						</Col>
						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Employment Type "
								options={constantDropdownOptions.employmentType}
								value={taxationDetails.employmentType}
								onChange={(value) => {
									handleChange(
										value,
										'employmentType',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Employment type is required"
								label="Employment Type"
								disabled={isDisabled}
								isError={formError.employmentType}
							/>
						</Col>
						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Enable Gross up tax "
								options={booleanOptions}
								value={String(taxationDetails.enableGrossUpTax)}
								onChange={(value) => {
									handleChange(
										value,
										'enableGrossUpTax',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Enable gross up tax is required"
								label="Enable Gross up tax?"
								disabled={isDisabled}
								isError={formError.enableGrossUpTax}
							/>
						</Col>
						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Employee with Disabilities"
								options={booleanOptions}
								value={String(taxationDetails.disabilities)}
								onChange={(value) => {
									handleChange(
										value,
										'disabilities',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Employee with Disabilities is required"
								label="Employee with Disabilities"
								disabled={isDisabled}
								isError={formError.disabilities}
							/>
						</Col>

						<Col span={6} className={styles['col']}>
							<SelectDropdown
								placeholder="Compensation Fund"
								options={booleanOptions}
								value={String(
									taxationDetails.isCompensationFund
								)}
								onChange={(value) => {
									handleChange(
										value,
										'isCompensationFund',
										'taxationDetails',
										true
									);
								}}
								size="large"
								required={true}
								helperText="Compensation Fund is required"
								label="Compensation Fund"
								disabled={isDisabled}
								isError={formError.isCompensationFund}
							/>
						</Col>
					</Row>
				</div>
			),
		},
	];

	useEffect(() => {
		if (
			employeeId &&
			!permissionsContext.allowedPermissions.includes('Edit_Employment')
		) {
			setIsDisabled(true);
		} else {
			setIsDisabled(false);
		}
	}, [employeeId, permissionsContext.allowedPermissions]);

	return (
		<>
			<div className={styles['form']}>
				<Collapse
					items={items}
					defaultActiveKey={['1', '2', '3']}
					expandIconPosition="end"
					className={styles['employeeDetails']}
					expandIcon={({ isActive }) => (
						<div
							style={{
								transform: `rotate(${isActive ? -90 : 0}deg)`,
								transition: '0.3s',
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								fill="currentColor"
								className="bi bi-chevron-right"
								viewBox="0 0 16 16"
							>
								<path
									fillRule="evenodd"
									d="M1.646 4.646a.5.5 0 0 1 0 .708l6 6a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708.708L2.707 4.5 1.646 5.561a.5.5 0 0 1-.708-.708z"
								/>
							</svg>
						</div>
					)}
					// onChange={onChangesss}
				/>
				<div className={styles['form-container']}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'start',
							margin: '1rem 0rem ',
						}}
					>
						<Buttons buttons={buttons} />
					</div>
				</div>
			</div>
			{showConfirmationModal && (
				<>
					<Modal
						open={showConfirmationModal}
						onCancel={closeConfirmationModal}
						footer={null}
						closable={false}
						className="confirmation-modal"
						centered={true}
						width={350}
						maskClosable={false}
					>
						<div className={`${styles['modal']} modal`}>
							<div className={styles['modal-body']}>
								<p className={styles['modal-text']}>
									{isTerminationDateChange &&
										"Would you like to encash leave before terminating the employee ? Click 'Yes' to proceed."}
								</p>
							</div>
							<div className={styles['modal-buttons']}>
								<Buttons buttons={confirmModalButtons} />
							</div>
						</div>
					</Modal>
				</>
			)}
		</>
	);
};

export default TaxationDetails;
